// Nomad-mon Go! — P3 type system: 6-type circular weakness wheel + keyword
// reminder text + minimal SVG glyphs (DOM only — reading text never in canvas).
// Wheel: IRON → VOYAGE → HUSTLE → MYSTIC → CHAOS → HEART → IRON.
// X → Y means X beats Y: Y takes 2× from X (weak), X takes −2 from Y (resist).

export const TYPE_ORDER = ['IRON', 'VOYAGE', 'HUSTLE', 'MYSTIC', 'CHAOS', 'HEART'];

const GLYPH_PATHS = {
  // barbell — strength / sport
  IRON: '<path d="M4 12h16" stroke-linecap="round"/><path d="M6.5 7.5 v 9 M17.5 7.5 v 9" stroke-linecap="round"/><path d="M3.2 9.5v5M20.8 9.5v5" stroke-linecap="round"/>',
  // compass — travel / outdoors
  VOYAGE: '<circle cx="12" cy="12" r="8.2"/><path d="M15.8 8.2l-2.5 6.1-3.6-3.6z" fill="currentColor" stroke="none"/>',
  // faceted gem — money / grind
  HUSTLE: '<path d="M12 4.2l6 5.5L12 19.8 6 9.7z" stroke-linejoin="round"/><path d="M6 9.7h12M12 4.2v15.6" opacity=".65"/>',
  // eye — weird / supernatural
  MYSTIC: '<path d="M2.6 12s3.7-5.6 9.4-5.6S21.4 12 21.4 12s-3.7 5.6-9.4 5.6S2.6 12 2.6 12z"/><circle cx="12" cy="12" r="2.4" fill="currentColor" stroke="none"/>',
  // bolt — mayhem / comedy
  CHAOS: '<path d="M13.4 3L5.6 13.6h4.6L9 21l8.4-11.2h-4.6z" fill="currentColor" stroke="none" stroke-linejoin="round"/>',
  // heart — charm / care
  HEART: '<path d="M12 19.6s-6.6-4.3-8.4-8.3C2.4 8.5 4.5 5 7.7 5c1.9 0 3.3 1 4.3 2.8C13 6 14.4 5 16.3 5c3.2 0 5.3 3.5 4.1 6.3-1.8 4-8.4 8.3-8.4 8.3z" fill="currentColor" stroke="none"/>',
};

export const TYPES = {
  IRON:   { label: 'Iron',   color: '#a8bfd0', identity: 'strength · sport' },
  VOYAGE: { label: 'Voyage', color: '#35d0ba', identity: 'travel · outdoors' },
  HUSTLE: { label: 'Hustle', color: '#f0c463', identity: 'money · grind' },
  MYSTIC: { label: 'Mystic', color: '#b98aff', identity: 'weird · supernatural' },
  CHAOS:  { label: 'Chaos',  color: '#ff6a4d', identity: 'mayhem · comedy' },
  HEART:  { label: 'Heart',  color: '#ff7ab8', identity: 'charm · care' },
};
for (let i = 0; i < TYPE_ORDER.length; i++) {
  const t = TYPE_ORDER[i];
  TYPES[t].beats = TYPE_ORDER[(i + 1) % 6];          // deals 2× to this type
  TYPES[t].weakTo = TYPE_ORDER[(i + 5) % 6];         // takes 2× from this type
  // resists the type it beats (−2 damage taken from it)
  TYPES[t].resists = TYPES[t].beats;
}

export function typeGlyph(type, size = 16, cls = '') {
  const t = TYPES[type];
  if (!t) return '';
  return `<svg class="tglyph ${cls}" viewBox="0 0 24 24" width="${size}" height="${size}" ` +
    `fill="none" stroke="currentColor" stroke-width="1.8" style="color:${t.color}" ` +
    `aria-label="${t.label}">${GLYPH_PATHS[type]}</svg>`;
}

// P5: the diarrhea-holo seal — a tasteful gold swirl sigil (never the raw emoji).
// Inline SVG so it inherits DOM scaling; brown-gold ring + spiral + three motes.
export function sigilSVG(size = 14, cls = '') {
  return `<svg class="vseal ${cls}" viewBox="0 0 24 24" width="${size}" height="${size}" aria-label="Diarrhea Holo">` +
    `<defs><radialGradient id="sgG" cx="38%" cy="32%"><stop offset="0%" stop-color="#f6e2a8"/>` +
    `<stop offset="55%" stop-color="#c9973c"/><stop offset="100%" stop-color="#5c3a12"/></radialGradient></defs>` +
    `<circle cx="12" cy="12" r="10.4" fill="url(#sgG)" stroke="#2e1c06" stroke-width="1.2"/>` +
    `<path d="M12 5.4c3.6 0 6.2 2.6 6.2 5.8 0 2.9-2.3 5-5.1 5-2.3 0-4.1-1.7-4.1-3.9 0-1.8 1.4-3.2 3.2-3.2 1.4 0 2.5 1 2.5 2.4 0 1-.8 1.8-1.8 1.8" ` +
    `fill="none" stroke="#3a230a" stroke-width="1.7" stroke-linecap="round"/>` +
    `<circle cx="7.4" cy="8.2" r="1" fill="#f6e2a8" opacity=".9"/>` +
    `<circle cx="16.8" cy="17.2" r=".8" fill="#f6e2a8" opacity=".7"/>` +
    `<circle cx="8.3" cy="16.6" r=".7" fill="#f6e2a8" opacity=".6"/></svg>`;
}

// SC2-B P6: PWR — ONE power formula for every display surface (pack reveal
// panel, fan nameplate chip, deck-picker meta). Character raw score weighs the
// whole kit: hp, basic, the engine-derived HEAVY (basic ×1.6, half-weighted —
// it costs 2 energy), the strongest other move at 0.7, and speed. Supports
// normally score through the same formula (they all carry hp/moves today); a
// card that ever ships without them falls back to its rarity band's raw
// midpoint so it still lands inside the band.
// The raw range is mapped to 1..99 with ONE fixed linear scale chosen from the
// shipped set (raw 68.5..173.5 -> PWR 18..98): PWR = round(raw*0.76 - 34).
export const PWR_SCALE = 0.76;
export const PWR_BIAS = -34;
const PWR_SUPPORT_RAW = { common: 77, rare: 97, epic: 116, legendary: 134, mythic: 160, eternal: 171 };

export function pwrRaw(d) {
  if (!(d.hp > 0) || !Array.isArray(d.moves) || !d.moves.length)
    return PWR_SUPPORT_RAW[d.rarity] ?? PWR_SUPPORT_RAW.common;
  const basic = d.moves[0].dmg || 0;
  const rest = d.moves.slice(1).map(m => m.dmg || 0);
  const special = rest.length ? Math.max(...rest) : 0;
  return d.hp * 0.35 + basic + (basic * 1.6) * 0.5 + special * 0.7 + (d.spd || 0) * 2;
}

// v11: FORGED cards (Duos / Fusions) are the level above god-tier, so their
// PWR is not capped at 99 — the top Fusion reads ~165 at level 1.
export function pwr(d) {
  return Math.max(1, Math.min(d && d.forged ? 250 : 99, Math.round(pwrRaw(d) * PWR_SCALE + PWR_BIAS)));
}

// SC15: card art URL — genesis/daily cards carry repo-relative paths
// ("assets/web/..."), GRAND ARCHIVE wave cards carry absolute art-proxy paths
// ("/api/nomadmon/art/<key>.webp", same-origin so WebGL textures stay clean).
export function artSrc(d, v, size) {
  if (d.art.startsWith('/')) return `${d.art}?${v}${size === 'thumb' ? '&s=thumb' : ''}`;
  return `./${d.art}?${v}`;
}

// SC21: clips arrive two ways — the hand-encoded set still lives beside the
// game (assets/clips/*.mp4, a relative path) while the ~200 cards wired to the
// TOTAL VIDEO INTEGRATION route carry a root-absolute /api/nomadmon/clip/ key.
// Same rule as artSrc: absolute stays absolute, relative gets ./ so it resolves
// under /nomadmon/ instead of the site root.
export function clipSrcUrl(clip, v) {
  if (!clip) return null;
  return clip.startsWith('/') ? `${clip}?${v}` : `./${clip}?${v}`;
}

export const KEYWORDS = {
  GUARD:     'Takes 3 less damage from basic moves.',
  SWIFT:     'Always moves first; ignores swap costs.',
  WARD:      'Ignores the first special move that hits it each battle.',
  LIFESTEAL: 'Heals HP equal to half the damage it deals.',
  STEALTH:   "Can't be targeted the round it enters the arena.",
  THORNS:    'Attackers take 5 damage back.',
  REGEN:     'Recovers 5 HP at the end of each round.',
  PIERCE:    'Its moves ignore GUARD and resistance.',
};
